import BaseWidget, { escapeHtml } from './baseWidget.js';
import stammdatenService from '../../services/stammdatenService.js';
import systemNachrichtService from '../../services/systemNachrichtService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { avatarFarbeAusName, initialenAusName } from '../../util.js';

const FILTER_ALL = 'all';
const FILTER_OHNE_BEREICH = '__ohne__';

/**
 * Listet alle Mitarbeiter auf, die aktuell eine offene Zeiterfassungsschicht
 * (eingestempelt, noch nicht ausgestempelt) im gewählten Betrieb haben.
 * Daten werden aus der Tagesübersichts-Route gezogen und mit Posten -> Bereich
 * aus dem Unternehmensobjekt angereichert.
 */
export default class ActiveEmployeeWidget extends BaseWidget {
    constructor(type, instanceId, state = {}) {
        super(type, instanceId, state);
        this.filter = state.filter || FILTER_ALL;
        this._items = [];
        this._bereichOptions = [];
    }

    serializeState() {
        return { filter: this.filter };
    }

    async fetchAktiveSchichten() {
        const betriebID = stammdatenService.aktuelleBetriebsstaette;
        if (!betriebID) return [];
        try {
            const response = await fetch(`/neolohn/api/tagesuebersicht/aktive/${betriebID}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await response.json();
        } catch (error) {
            console.log(error);
            systemNachrichtService.zeigeKleineNachricht('Fehler beim Laden der aktiven Mitarbeiter', -1);
            return [];
        }
    }

    /**
     * Baut eine PostenID -> { Posten, Bereich } Map und sammelt alle Bereiche
     * die in der aktuellen Betriebsstätte überhaupt vorkommen.
     */
    buildPostenIndex(unternehmen) {
        const betrieb = unternehmen?.Betriebsstaette?.find(
            (b) => String(b._id) === String(stammdatenService.aktuelleBetriebsstaette),
        );
        const bereichById = new Map(
            (unternehmen?.Bereiche || []).map((b) => [String(b._id), b.Bereich]),
        );
        const postenById = new Map();
        const bereicheImBetrieb = new Set();
        (betrieb?.Posten || []).forEach((p) => {
            const bereich = bereichById.get(String(p.BereichID)) || '';
            postenById.set(String(p._id), { Posten: p.Posten, Bereich: bereich });
            if (bereich) bereicheImBetrieb.add(bereich);
        });
        return { postenById, bereicheImBetrieb };
    }

    async onRender() {
        this.setLoading();
        const [schichten, unternehmen] = await Promise.all([
            this.fetchAktiveSchichten(),
            stammdatenService.holeUnternehmensdaten(),
        ]);
        const { postenById, bereicheImBetrieb } = this.buildPostenIndex(unternehmen);
        const jetzt = dayjs();

        const aktive = schichten
            .map((schicht) => {
                const postenInfo = postenById.get(String(schicht.PostenID)) || {};
                const von = dayjs(schicht.SchichtVon);
                const minuten = Math.max(0, jetzt.diff(von, 'minutes'));
                const avatarSrc = schicht.Avatar?.base64
                    ? `data:${schicht.Avatar.MimeType || 'image/png'};base64,${schicht.Avatar.base64}`
                    : '';
                return {
                    maId: schicht.MitarbeiterID,
                    name: schicht.Name,
                    posten: schicht.Posten || postenInfo.Posten || '',
                    bereich: postenInfo.Bereich || '',
                    schichtVon: von,
                    minuten,
                    avatarSrc,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, 'de'));

        this._items = aktive;
        // Filterchips zeigen alle Bereiche der aktuellen Betriebsstätte (nicht nur die der aktiv eingestempelten),
        // damit der Filter immer sichtbar/bedienbar ist.
        this._bereichOptions = [...bereicheImBetrieb].sort((a, b) => a.localeCompare(b, 'de'));
        this._hatOhneBereich = aktive.some((item) => !item.bereich);

        this.bodyEl.innerHTML = `
            <div class="widget-deadline-filter widget-active-ma-filter"></div>
            <div class="widget-active-ma-list"></div>
        `;
        this.renderFilterChips();
        this.renderList();
    }

    renderFilterChips() {
        const filterEl = this.bodyEl.querySelector('.widget-active-ma-filter');
        if (!filterEl) return;
        // Ohne Bereiche im Betrieb gibt es nichts zu filtern
        if (this._bereichOptions.length === 0 && !this._hatOhneBereich) {
            filterEl.remove();
            return;
        }
        const chips = [{ value: FILTER_ALL, label: 'Alle Bereiche' }];
        this._bereichOptions.forEach((b) => chips.push({ value: b, label: b }));
        if (this._hatOhneBereich) chips.push({ value: FILTER_OHNE_BEREICH, label: 'Ohne Bereich' });

        filterEl.innerHTML = chips.map((c) => `
            <button type="button" class="deadline-chip${this.filter === c.value ? ' active' : ''}" data-filter="${escapeHtml(c.value)}">
                ${escapeHtml(c.label)}
            </button>
        `).join('');
        filterEl.querySelectorAll('.deadline-chip').forEach((chip) => {
            chip.addEventListener('click', () => {
                this.filter = chip.dataset.filter;
                filterEl.querySelectorAll('.deadline-chip').forEach((c) => c.classList.toggle('active', c.dataset.filter === this.filter));
                this.renderList();
                if (window.myServices?.dashboard) {
                    window.myServices.dashboard.saveConfig();
                }
            });
        });
    }

    renderList() {
        const listEl = this.bodyEl.querySelector('.widget-active-ma-list');
        if (!listEl) return;

        let filtered = this._items;
        if (this.filter === FILTER_OHNE_BEREICH) {
            filtered = this._items.filter((i) => !i.bereich);
        } else if (this.filter !== FILTER_ALL) {
            filtered = this._items.filter((i) => i.bereich === this.filter);
        }

        if (filtered.length === 0) {
            listEl.innerHTML = '<div class="widget-empty">Keine eingestempelten Mitarbeiter.</div>';
            return;
        }

        listEl.innerHTML = '';
        filtered.forEach((item) => {
            const row = document.createElement('div');
            row.className = 'widget-active-ma-item';
            const meta = [item.bereich, item.posten].filter(Boolean).join(' · ');
            const langeSchicht = item.minuten >= 600 && item.minuten < 1200;
            const extremeSchicht = item.minuten >= 1200;
            let durationClass = '';
            let durationTitle = `Eingestempelt seit ${item.schichtVon.format('DD.MM.YYYY, HH:mm')}`;
            if (extremeSchicht) {
                durationClass = ' active-ma-duration-alert';
                durationTitle += ' – mehr als 20 Std. eingestempelt! Vermutlich vergessen auszustempeln.';
            } else if (langeSchicht) {
                durationClass = ' active-ma-duration-warn';
                durationTitle += ' – über 10 Std. eingestempelt';
            }
            // Name liegt als "Nachname, Vorname" vor. Getrennt halten für korrekte Initialen,
            // und als "Vorname Nachname" zusammensetzen, damit Farbe mit der Mitarbeiter-Übersicht übereinstimmt.
            const [nachname = '', vorname = ''] = (item.name || '').split(',').map((teil) => teil.trim());
            const naturalName = `${vorname} ${nachname}`.trim();
            const farbe = avatarFarbeAusName(naturalName);
            const avatarEl = item.avatarSrc
                ? `<img class="active-ma-avatar" src="${escapeHtml(item.avatarSrc)}" alt="" />`
                : `<div class="active-ma-avatar active-ma-avatar-fallback" style="--ma-avatar-bg:${farbe.bg};--ma-avatar-fg:${farbe.fg}">${escapeHtml(initialenAusName(vorname, nachname))}</div>`;
            row.innerHTML = `
                ${avatarEl}
                <div class="active-ma-content">
                    <div class="active-ma-name">${escapeHtml(item.name)}</div>
                    <div class="active-ma-meta">${escapeHtml(meta || '—')}</div>
                </div>
                <div class="active-ma-duration${durationClass}" title="${escapeHtml(durationTitle)}">
                    ${extremeSchicht ? '<i class="bi bi-exclamation-triangle-fill"></i> ' : ''}${formatDauer(item.minuten)}
                </div>
            `;
            row.addEventListener('click', async () => {
                if (item.maId && window.myHandlers?.selectMA) {
                    await window.myHandlers.selectMA(item.maId, 'Zeiten');
                }
            });
            listEl.appendChild(row);
        });
    }
}

function formatDauer(minuten) {
    if (minuten >= 1440) {
        const tage = Math.floor(minuten / 1440);
        const restStd = Math.floor((minuten % 1440) / 60);
        const tagLabel = tage === 1 ? 'Tag' : 'Tage';
        return restStd === 0 ? `${tage} ${tagLabel}` : `${tage} ${tagLabel} ${restStd} Std`;
    }
    const h = Math.floor(minuten / 60);
    const m = minuten % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} Std`;
    return `${h} Std ${m} min`;
}
